import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  AccountCreationErrorMessage,
  CheckedCircle,
  ConfirmVerificationCode,
  CreateAccountButton,
  CreateAccountButtonContainer,
  CreateAccountContainer,
  CreateAccountHeader,
  CreateAccountLink,
  CreateAccountMessageLabel,
  CreateAccountStepOneContainer,
  CreateAccountStepTwoContainer,
  EmailVerificationCodeInput,
  HelpText,
  PasswordConstraintMessageLabel,
  RegistrationFieldContainer,
  RegistrationFieldHeader,
  RegistrationFieldPasswordInputField,
  RegistrationFieldTextInputField,
  RequiredField,
  TermsAndConditionsContainer,
  TextLink,
  UncheckedCircle,
} from './styles';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  getCustomersWithCrossLinkToken,
  getRegistrationFields,
  registerCustomer,
  updateUserCredentials,
  verifyConfirmationCode,
  verifyRegistrationFields,
} from './createAccountApi';
import Loader from '../../common/loader';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import { FooterContainer, LoggedOutTemplateLogo, LogoLink } from '../login/styles';
import {
  setAccountCreationErrors,
  setAccountCreationSuccessful,
  setCrossLinkRegistrationToken,
  setErrors,
  setRegisterSuccessful,
  setRegistrationToken,
  setVerifyCodeSuccessful,
} from '../login/loginReducer';
import Footer from '../dashboard/footer';
import { getPolicyInfoInputField } from '../../../utils/fields';
import { getText } from '../../../utils/i18n';
import TooltipInfo from '../../common/tooltip';
import { Modal } from '../../common/modal-with-html';
import termsHtml from '../../../config/documents/tos.html';
import privacyHtml from '../../../config/documents/privacy.html';
import { isEmail } from '../../../utils/validators/generalValidators';

import { Dialog } from '@mui/material';
import { useTheme } from 'styled-components';
import { ga4Event } from '../../../utils/google-analytics-4';
import { StyledCheckIcon } from '../user-profile/styles';
import { IconSpan } from '../../../utils/fields/styles';
import { ModalContainer, ModalHeader } from '../enable-mfa/styles';

export const CreateAccount = (props) => {
  const {
    registrationFields,
    registrationToken,
    isAccountCreationSuccessful,
    isRegisterSuccessful,
    isVerifyCodeSuccessful,
    crossLinkRegistrationToken,
    accountCreationErrors,
    i18n,
    affiliatedCarrierAccess,
    companyName,
    isCustomerEmailFound,
    customPrivacyStatement,
    customTOS,
    customerEmailOnFile,
    policyStatusCd,
    verifyFlow,
  } = props;

  const theme = useTheme();
  const [answers, setAnswers] = useState({});
  const [showStepTwo, setShowStepTwo] = useState(false);
  const [passwordConstraints, setPasswordConstraints] = useState(
    props.state.configurationSlice.features.passwordConstraints,
  );
  const [userIdConstraints, setUserIdConstraints] = useState(
    props.state.configurationSlice.features.userIdConstraints,
  );

  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [isConditionsAccepted, setConditionAccepted] = useState(false);
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isCreateAccountButtonSubmitting, setCreateAccountButtonSubmitting] = useState(false);
  const [isRegisterButtonSubmitting, setRegisterButtonSubmitting] = useState(false);
  const [isVerifyCodeButtonSubmitting, setVerifyCodeButtonSubmitting] = useState(false);
  const [termsAndConditionsDialogOpen, setTermsAndConditionsDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [oktaUserId, setOktaUserId] = useState('');

  const { enqueueSnackbar } = useSnackbar();

  const areRequiredFieldsCompleted = () => {
    if (
      registrationFields.length === 0 ||
      Object.entries(answers).length !== registrationFields.length
    )
      return false;
    // eslint-disable-next-line no-unused-vars
    for (let value of Object.values(answers)) {
      if (!value.fieldComplete) return false;
    }
    if (verifyFlow) {
      return isEmail(verificationEmail);
    } else {
      return true;
    }
  };

  const submitVerificationCode = (verificationCode) => {
    setVerifyCodeButtonSubmitting(true);
    props.dispatch(
      verifyConfirmationCode(
        oktaUserId,
        verificationCode,
        registrationToken,
        setVerifyCodeButtonSubmitting,
        enqueueSnackbar,
      ),
    );
  };

  const determineVerifyRegistrationPayload = (registrationAnswers, verifyEmail) => {
    return affiliatedCarrierAccess
      ? {
          optionKey: 'serviceportal',
          allowedPolicyStatusCd: policyStatusCd,
          registrationInfo: registrationAnswers,
          email: verificationEmail,
          carrier: theme.carrierName,
        }
      : {
          optionKey: 'serviceportal',
          allowedPolicyStatusCd: policyStatusCd,
          registrationInfo: registrationAnswers,
          email: verificationEmail,
        };
  };

  const determineCreateAccountPayload = (tempUser) => {
    const payload = affiliatedCarrierAccess
      ? {
          registrationToken: crossLinkRegistrationToken
            ? crossLinkRegistrationToken
            : registrationToken,
          loginId: userId,
          password: password,
          emailAddr: verifyFlow ? verificationEmail : email,
          termsAccepted: isConditionsAccepted,
          carrierGroup: theme.carrierName,
        }
      : {
          registrationToken: crossLinkRegistrationToken
            ? crossLinkRegistrationToken
            : registrationToken,
          loginId: userId,
          password: password,
          emailAddr: verifyFlow ? verificationEmail : email,
          termsAccepted: isConditionsAccepted,
        };
    payload['tempUser'] = tempUser;
    return payload;
  };

  const createAccountButtonClickHandler = () => {
    setRegisterButtonSubmitting(true);
    // map question as name and answer as value
    const registrationAnswers = [];
    for (let i = 0; i < registrationFields.length; i++) {
      registrationAnswers[i] = { name: registrationFields[i].value, value: answers[i].answer };
    }

    const payload = determineVerifyRegistrationPayload(registrationAnswers, verificationEmail);
    if (verifyFlow) {
      props.dispatch(
        verifyRegistrationFields(
          payload,
          enqueueSnackbar,
          false,
          determineCreateAccountPayload(true),
          setOktaUserId,
          setRegisterButtonSubmitting,
        ),
      );
    } else {
      props.dispatch(
        verifyRegistrationFields(
          payload,
          enqueueSnackbar,
          false,
          false,
          setOktaUserId,
          setRegisterButtonSubmitting,
        ),
      );
    }
  };

  const createAccountHandler = () => {
    setCreateAccountButtonSubmitting(true);
    const payload = determineCreateAccountPayload(false);
    if (verifyFlow) {
      props.dispatch(
        updateUserCredentials(
          oktaUserId,
          userId,
          password,
          registrationToken,
          isConditionsAccepted,
          email,
          setCreateAccountButtonSubmitting,
          enqueueSnackbar,
        ),
      );
    } else {
      props.dispatch(registerCustomer(payload, enqueueSnackbar, setOktaUserId, false));
    }
  };

  const validatePassword = (passwordValue) => {
    const updatePasswordConstraints = [];
    passwordConstraints.forEach((passwordConstraint) => {
      const item = { ...passwordConstraint };
      const constraint = item.constraint;
      const value = item.value;
      switch (constraint) {
        case 'minLength':
          item.valid = passwordValue.length >= value;
          break;
        case 'minNumber':
          item.valid = (passwordValue.match(/\d/g) || []).length >= value;
          break;
        case 'minLowerCase':
          item.valid = (passwordValue.match(/[a-z]/g) || []).length >= value;
          break;
        case 'minUpperCase':
          item.valid = (passwordValue.match(/[A-Z]/g) || []).length >= value;
          break;
        default:
          item.valid = true;
          break;
      }
      updatePasswordConstraints.push(item);
    });
    setPasswordConstraints(updatePasswordConstraints);
  };

  const validateUserId = (userIdValue) => {
    const updateUserIdConstraints = [];
    userIdConstraints.forEach((userIdConstraint) => {
      const item = { ...userIdConstraint };
      const constraint = item.constraint;
      const value = item.value;
      if (constraint === 'minLength') {
        item.valid = userIdValue.length >= value;
      } else {
        item.valid = true;
      }

      updateUserIdConstraints.push(item);
    });
    setUserIdConstraints(updateUserIdConstraints);
  };

  const areAccountCreationFieldsComplete = () => {
    let completeFlag = true;
    completeFlag = completeFlag && isConditionsAccepted;
    completeFlag =
      completeFlag &&
      userIdConstraints.filter((constraint) => constraint && constraint.valid === true).length ===
        userIdConstraints.length;
    completeFlag =
      completeFlag &&
      passwordConstraints.filter((constraint) => constraint && constraint.valid === true).length ===
        passwordConstraints.length;
    completeFlag = completeFlag && password === confirmPassword;
    completeFlag = completeFlag && isEmail(email);

    return completeFlag;
  };

  const handleGotoStepOne = () => {
    props.dispatch(getRegistrationFields(enqueueSnackbar));
    props.dispatch(setRegistrationToken(null));
    props.dispatch(setCrossLinkRegistrationToken(null));
    props.dispatch(setErrors(null));
    props.dispatch(setAccountCreationErrors(null));
    setRegisterSuccessful(undefined);
    setVerifyCodeSuccessful(undefined);
    setShowStepTwo(false);
    setUserId('');
    setPassword('');
    setConfirmPassword('');
    setConditionAccepted(false);
  };

  const navigate = useNavigate();

  const handleModalClose = () => {
    props.dispatch(setRegisterSuccessful(false));
    setOpenModal(false);
  };

  useEffect(() => {
    ga4Event({ category: 'create-account', action: 'page-view' });
    props.dispatch(getRegistrationFields(enqueueSnackbar));
    props.dispatch(setRegisterSuccessful(false));
    props.dispatch(setAccountCreationSuccessful(false));
    setShowStepTwo(false);
    setConditionAccepted(false);
    setOpenModal(false);
  }, []);

  useEffect(() => {
    if (registrationToken && registrationToken.length > 0 && !verifyFlow) {
      setShowStepTwo(true);
    }
  }, [registrationToken]);

  useEffect(() => {
    if (crossLinkRegistrationToken && crossLinkRegistrationToken.length > 0) {
      props.dispatch(getCustomersWithCrossLinkToken(crossLinkRegistrationToken, enqueueSnackbar));
    }
  }, [crossLinkRegistrationToken]);

  useEffect(() => {
    if (isRegisterSuccessful) {
      setRegisterButtonSubmitting(false);
      setOpenModal(true);
    } else {
      setOpenModal(false);
      setRegisterButtonSubmitting(false);
      props.dispatch(setRegisterSuccessful(undefined));
    }
  }, [isRegisterSuccessful]);

  useEffect(() => {
    if (isVerifyCodeSuccessful) {
      setOpenModal(false);
      setShowStepTwo(true);
    }
    props.dispatch(setVerifyCodeSuccessful(undefined));
  }, [isVerifyCodeSuccessful]);

  useEffect(() => {
    if (isAccountCreationSuccessful) {
      setShowStepTwo(false);
      props.dispatch(setRegistrationToken(''));
      props.dispatch(setCrossLinkRegistrationToken(''));
      props.dispatch(setAccountCreationSuccessful(undefined));
      props.dispatch(setRegisterSuccessful(undefined));
      props.dispatch(setVerifyCodeSuccessful(undefined));
      // navigating to '/' re-renders okta widget, '/login' does not
      navigate('/');
    } else {
      setCreateAccountButtonSubmitting(false);
      props.dispatch(setAccountCreationSuccessful(undefined));
    }
  }, [isAccountCreationSuccessful]);

  // when customer email is updated but okta registration fails, clear the email
  useEffect(() => {
    if (isCustomerEmailFound) {
      if (verifyFlow) {
        setEmail(verificationEmail);
      } else {
        setEmail('');
      }
    } else {
      setEmail('');
    }
  }, [isCustomerEmailFound]);

  return (
    <>
      <Helmet>
        <title>Create Account</title>
      </Helmet>
      <LogoLink to="/dashboard" id="createAccountLoggedOutLogoLink">
        <LoggedOutTemplateLogo
          src={
            affiliatedCarrierAccess
              ? 'config/images/' + theme.carrierName + '/loggedout-logo.png'
              : `config/images/loggedout-logo.png`
          }
          alt={companyName + ' logo'}
        />
      </LogoLink>
      <CreateAccountContainer>
        {!showStepTwo ? (
          <CreateAccountStepOneContainer>
            <CreateAccountHeader id="createAccountStepOneHeader">
              Create Account - Step One
            </CreateAccountHeader>
            <CreateAccountMessageLabel>
              Please answer the following security questions:
            </CreateAccountMessageLabel>
            {registrationFields.length === 0 ? (
              <Loader />
            ) : (
              registrationFields.map((registrationField, index) => (
                <RegistrationFieldContainer
                  key={index}
                  id={registrationField.name + 'FieldContainer'}
                >
                  <RegistrationFieldHeader>
                    {registrationField.name}
                    <RequiredField>*</RequiredField>
                    <TooltipInfo
                      title={getText(i18n, 'createAccount.' + registrationField.name + '.Tooltip')}
                    />{' '}
                    <HelpText>
                      {getText(i18n, 'signup.helpText.' + registrationField.value)}
                    </HelpText>
                  </RegistrationFieldHeader>
                  {getPolicyInfoInputField(
                    registrationField.value,
                    index,
                    answers,
                    setAnswers,
                    'CreateAccount',
                  )}
                </RegistrationFieldContainer>
              ))
            )}
            {verifyFlow && (
              <RegistrationFieldContainer>
                <RegistrationFieldHeader>
                  {getText(i18n, 'createAccount.verificationEmail')}
                  <RequiredField>*</RequiredField>
                  <TooltipInfo
                    title={getText(i18n, 'createAccount.VerificationEmailTooltip')}
                  />{' '}
                  <HelpText>{getText(i18n, 'signup.helpText.VerificationEmail')}</HelpText>
                </RegistrationFieldHeader>
                <div>
                  <RegistrationFieldTextInputField
                    id="verificationEmail"
                    onChange={(e) => {
                      setVerificationEmail(e.target.value);
                    }}
                    autoComplete="email"
                    value={verificationEmail}
                  />
                  {isEmail(verificationEmail) ? (
                    <IconSpan>
                      <StyledCheckIcon id="email" />
                    </IconSpan>
                  ) : null}
                </div>
              </RegistrationFieldContainer>
            )}
            <CreateAccountButtonContainer>
              {isRegisterButtonSubmitting ? (
                <Loader />
              ) : (
                <CreateAccountButton
                  disabled={!areRequiredFieldsCompleted()}
                  onClick={() => {
                    createAccountButtonClickHandler();
                  }}
                >
                  Register
                </CreateAccountButton>
              )}
            </CreateAccountButtonContainer>
            <Dialog open={openModal} onClose={handleModalClose}>
              <ModalContainer>
                <>
                  <ModalHeader>Confirm Email Address</ModalHeader>
                  <div>Please enter the verification code sent to {verificationEmail}.</div>
                  <EmailVerificationCodeInput
                    type="text"
                    onChange={(e) => setEmailVerificationCode(e.target.value)}
                  />
                  {isVerifyCodeButtonSubmitting ? (
                    <Loader />
                  ) : (
                    <ConfirmVerificationCode
                      onClick={() => submitVerificationCode(emailVerificationCode)}
                    >
                      Submit
                    </ConfirmVerificationCode>
                  )}
                </>
              </ModalContainer>
            </Dialog>
          </CreateAccountStepOneContainer>
        ) : (
          <CreateAccountStepTwoContainer>
            {accountCreationErrors && (
              <AccountCreationErrorMessage>
                There was an error creating your account. You can start from beginning by clicking{' '}
                <span>
                  <CreateAccountLink
                    onClick={() => {
                      handleGotoStepOne();
                    }}
                  >
                    here
                  </CreateAccountLink>
                </span>
                {'.'}
              </AccountCreationErrorMessage>
            )}
            <CreateAccountHeader id="createAccountStepOneHeader">
              Create Account - Step Two
            </CreateAccountHeader>
            <CreateAccountMessageLabel>
              {getText(i18n, 'createAccount.stepTwoInstructions')}
            </CreateAccountMessageLabel>
            <form>
              <RegistrationFieldContainer>
                <RegistrationFieldHeader>
                  User ID <TooltipInfo title={getText(i18n, 'createAccount.userIdTooltip')} />
                </RegistrationFieldHeader>
                <RegistrationFieldTextInputField
                  id="userId"
                  onChange={(e) => {
                    validateUserId(e.target.value);
                    setUserId(e.target.value);
                  }}
                  autoComplete="username"
                />
              </RegistrationFieldContainer>
              {userId.length > 0 &&
                userIdConstraints &&
                userIdConstraints.map((constraint) => (
                  <PasswordConstraintMessageLabel key={constraint.constraint}>
                    {constraint.message}
                  </PasswordConstraintMessageLabel>
                ))}
              <RegistrationFieldContainer>
                <RegistrationFieldHeader>
                  Password <TooltipInfo title={getText(i18n, 'createAccount.passwordTooltip')} />
                </RegistrationFieldHeader>
                <RegistrationFieldPasswordInputField
                  id="password"
                  onChange={(e) => {
                    validatePassword(e.target.value);
                    setPassword(e.target.value);
                  }}
                  autoComplete="new-password"
                />
                {password.length > 0 &&
                  passwordConstraints &&
                  passwordConstraints.map((constraint) => (
                    <PasswordConstraintMessageLabel key={constraint.constraint}>
                      {constraint.message}
                    </PasswordConstraintMessageLabel>
                  ))}
              </RegistrationFieldContainer>
              <RegistrationFieldContainer>
                <RegistrationFieldHeader>
                  Confirm Password{' '}
                  <TooltipInfo title={getText(i18n, 'createAccount.confirmPasswordTooltip')} />
                </RegistrationFieldHeader>
                <RegistrationFieldPasswordInputField
                  id="confirmPassword"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <PasswordConstraintMessageLabel>
                  {confirmPassword.length > 0 && (
                    <>
                      {password === confirmPassword ? <CheckedCircle /> : <UncheckedCircle />}{' '}
                      Passwords must match
                    </>
                  )}
                </PasswordConstraintMessageLabel>
              </RegistrationFieldContainer>
              <RegistrationFieldContainer>
                <RegistrationFieldHeader>
                  {getText(i18n, 'createAccount.registrationEmail')}
                  <TooltipInfo title={getText(i18n, 'createAccount.emailTooltip')} />
                </RegistrationFieldHeader>
                <RegistrationFieldTextInputField
                  id="registrationEmail"
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  autoComplete="email"
                  value={email}
                />
              </RegistrationFieldContainer>
            </form>
            <TermsAndConditionsContainer>
              <input
                type="checkbox"
                checked={isConditionsAccepted}
                onChange={() => {
                  setConditionAccepted(!isConditionsAccepted);
                }}
              />
              <span>
                {' '}
                I have read and accept the{' '}
                <TextLink
                  onClick={() => {
                    setTermsAndConditionsDialogOpen(true);
                  }}
                >
                  {getText(i18n, 'general.termsOfServiceTitle', 'Terms of Service')}
                </TextLink>{' '}
                and{' '}
                <TextLink
                  onClick={() => {
                    setPrivacyDialogOpen(true);
                  }}
                >
                  {getText(i18n, 'general.privacyStatementTitle', 'Privacy Statement')}
                </TextLink>
                .
                <Modal
                  modalHtml={termsHtml}
                  dialogOpen={termsAndConditionsDialogOpen}
                  customModal={customTOS}
                  modalTitle={getText(i18n, 'general.termsOfServiceTitle', 'Terms of Service')}
                  setDialogClosed={() => setTermsAndConditionsDialogOpen(false)}
                />
                <Modal
                  modalHtml={privacyHtml}
                  dialogOpen={privacyDialogOpen}
                  customModal={customPrivacyStatement}
                  modalTitle={getText(i18n, 'general.privacyStatementTitle', 'Privacy Statement')}
                  setDialogClosed={() => setPrivacyDialogOpen(false)}
                />
              </span>
            </TermsAndConditionsContainer>
            <CreateAccountButtonContainer>
              {isCreateAccountButtonSubmitting ? (
                <Loader />
              ) : (
                <CreateAccountButton
                  disabled={!areAccountCreationFieldsComplete()}
                  onClick={() => {
                    createAccountHandler(false);
                  }}
                >
                  Create Account
                </CreateAccountButton>
              )}
            </CreateAccountButtonContainer>
          </CreateAccountStepTwoContainer>
        )}
      </CreateAccountContainer>

      <FooterContainer>
        <Footer />
      </FooterContainer>
    </>
  );
};
CreateAccount.propTypes = {
  dispatch: PropTypes.any,
  registrationFields: PropTypes.array,
  registrationToken: PropTypes.string,
  crossLinkRegistrationToken: PropTypes.string,
  isAccountCreationSuccessful: PropTypes.bool,
  isRegisterSuccessful: PropTypes.bool,
  isVerifyCodeSuccessful: PropTypes.bool,
  accountCreationErrors: PropTypes.string,
  state: PropTypes.object,
  i18n: PropTypes.object,
  affiliatedCarrierAccess: PropTypes.bool,
  companyName: PropTypes.string,
  isCustomerEmailFound: PropTypes.bool,
  customPrivacyStatement: PropTypes.bool,
  customTOS: PropTypes.bool,
  customerEmailOnFile: PropTypes.string,
  customerId: PropTypes.string,
  policyStatusCd: PropTypes.string,
  verifyFlow: PropTypes.bool,
};

export const mapStateToProps = (state) => ({
  registrationFields: state.loginSlice.registrationFields,
  accountCreationErrors: state.loginSlice.accountCreationErrors,
  registrationToken: state.loginSlice.registrationToken,
  policyStatusCd: state.configurationSlice.features.policyStatusCd,
  crossLinkRegistrationToken: state.loginSlice.crossLinkRegistrationToken,
  isAccountCreationSuccessful: state.loginSlice.isAccountCreationSuccessful,
  isRegisterSuccessful: state.loginSlice.isRegisterSuccessful,
  isVerifyCodeSuccessful: state.loginSlice.isVerifyCodeSuccessful,
  i18n: state.i18nSlice.i18n,
  companyName: state.configurationSlice.features.companyName,
  customPrivacyStatement: state.configurationSlice.features.features.customPrivacyStatement,
  affiliatedCarrierAccess: state.configurationSlice.features.features.affiliatedCarrierAccess,
  customTOS: state.configurationSlice.features.features.customTOS,
  isCustomerEmailFound: state.loginSlice.isCustomerEmailFound,
  customerEmailOnFile: state.loginSlice.customerEmailOnFile,
  customerId: state.loginSlice.customerId,
  verifyFlow: state.configurationSlice.features.features.verifyFlow,
  state,
});

export default connect(mapStateToProps)(CreateAccount);
